import React, { useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { cn } from '../utils/cn';
import { Button } from './ui/Button';

export const SettingsModal = ({ isOpen, onClose }) => {
    const { language, setLanguage, vibrationEnabled, toggleVibration, t } = useLanguage();
    const modalRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const languages = [
        { code: 'en', label: t('language_en'), flag: '🇬🇧' },
        { code: 'ru', label: t('language_ru'), flag: '🇷🇺' },
        { code: 'uk', label: t('language_uk'), flag: '🇺🇦' }
    ];

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
            <div
                ref={modalRef}
                className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-3xl p-6 shadow-2xl relative overflow-hidden animate-slide-up"
            >
                {/* Background Glow */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

                {/* Header */}
                <div className="flex items-center justify-between mb-6 relative z-10">
                    <h2 className="text-xl font-bold text-white tracking-wide">{t('settings_title')}</h2>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 text-slate-400 hover:text-white transition-colors"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div className="space-y-6 relative z-10">
                    {/* Language Selector */}
                    <div className="space-y-3">
                        <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">{t('language')}</label>
                        <div className="grid grid-cols-1 gap-2">
                            {languages.map((lang) => (
                                <button
                                    key={lang.code}
                                    onClick={() => setLanguage(lang.code)}
                                    className={cn(
                                        "flex items-center justify-between p-3 rounded-xl border transition-all",
                                        language === lang.code
                                            ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.1)]"
                                            : "bg-white/5 border-white/5 text-slate-300 hover:bg-white/10"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-xl">{lang.flag}</span>
                                        <span className="font-medium">{lang.label}</span>
                                    </div>
                                    {language === lang.code && (
                                        <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_cyan]" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Vibration Toggle */}
                    <div className="space-y-3">
                        <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">{t('vibration')}</label>
                        <button
                            onClick={toggleVibration}
                            className="w-full flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group"
                        >
                            <span className="text-slate-200 font-medium flex items-center gap-2">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={vibrationEnabled ? "text-cyan-400" : "text-slate-500"}>
                                    <path d="M6 3v18" />
                                    <path d="M18 3v18" />
                                    <path d="M22 7v10" />
                                    <path d="M2 7v10" />
                                    <path d="M10 8l4 4-4 4" fill={vibrationEnabled ? "currentColor" : "none"} stroke="none" />
                                    <path d="M12 7v10" className={vibrationEnabled ? "hidden" : "block"} />
                                    <circle cx="12" cy="12" r="2" className={vibrationEnabled ? "block" : "hidden"} fill="currentColor" stroke="none" />
                                </svg>
                                {t('vibration')}
                            </span>
                            <div className={cn(
                                "w-12 h-6 rounded-full relative transition-colors duration-300",
                                vibrationEnabled ? "bg-cyan-500/30 border border-cyan-500/50" : "bg-slate-700/50 border border-slate-600/50"
                            )}>
                                <div className={cn(
                                    "absolute top-0.5 w-4 h-4 rounded-full transition-all duration-300 shadow-sm",
                                    vibrationEnabled
                                        ? "left-[calc(100%-1.25rem)] bg-cyan-400 shadow-[0_0_10px_cyan]"
                                        : "left-1 bg-slate-400"
                                )} />
                            </div>
                        </button>
                    </div>

                    {/* Footer Actions */}
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={onClose}
                        className="w-full font-bold tracking-widest mt-4"
                    >
                        {t('save')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
